import React, { useEffect, useState } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { LogoMark } from "./LogoMark";

const NAV_LINKS = [
  { id: "home", key: "home" },
  { id: "chi-siamo", key: "about" },
  { id: "scarica-catalogo", key: "catalog" },
  { id: "contatti", key: "contact" },
];

export const Navbar = () => {
  const { lang, setLang, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-[#1a3a2a]/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="pb-container flex items-center justify-between">
        {/* Wordmark with logo mark */}
        <button
          onClick={() => scrollTo("home")}
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span data-testid="nav-logo-mark" aria-hidden="true">
            <LogoMark size={scrolled ? 52 : 60} />
          </span>
          <span className="flex items-baseline gap-2">
            <span
              className={`font-heading font-black text-base md:text-lg tracking-tight uppercase transition-colors ${
                scrolled ? "text-[#1a3a2a]" : "text-white"
              }`}
            >
              Punteria
            </span>
            <span
              className={`font-heading font-light text-base md:text-lg tracking-tight uppercase transition-colors ${
                scrolled ? "text-[#1a3a2a]" : "text-white"
              }`}
            >
              Bartesaghi
            </span>
            <span className="hidden md:inline-block w-1.5 h-1.5 bg-[#c9a84c] mb-1 group-hover:scale-150 transition-transform" />
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className={`font-body text-sm font-medium hover:text-[#c9a84c] transition-colors uppercase tracking-wider ${
                scrolled ? "text-[#1a3a2a]" : "text-white"
              }`}
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-4">
          {/* Language switcher — refined, properly aligned */}
          <div
            data-testid="language-switcher"
            className={`hidden sm:inline-flex items-stretch border transition-colors ${
              scrolled ? "border-[#1a3a2a]/25" : "border-white/40"
            }`}
          >
            <span
              className={`flex items-center justify-center px-3 border-r ${
                scrolled ? "border-[#1a3a2a]/15" : "border-white/25"
              }`}
            >
              <Globe
                className={`w-4 h-4 ${scrolled ? "text-[#1a3a2a]" : "text-white"}`}
                strokeWidth={1.5}
              />
            </span>
            <button
              data-testid="lang-it"
              onClick={() => setLang("it")}
              className={`min-w-[44px] px-2 text-xs font-semibold tracking-[0.15em] transition-colors ${
                lang === "it"
                  ? "bg-[#c9a84c] text-[#1a3a2a]"
                  : scrolled
                    ? "text-[#1a3a2a] hover:bg-[#f8f9f8]"
                    : "text-white hover:bg-white/10"
              }`}
            >
              IT
            </button>
            <span
              className={`w-px ${scrolled ? "bg-[#1a3a2a]/15" : "bg-white/25"}`}
              aria-hidden="true"
            />
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`min-w-[44px] px-2 text-xs font-semibold tracking-[0.15em] transition-colors ${
                lang === "en"
                  ? "bg-[#c9a84c] text-[#1a3a2a]"
                  : scrolled
                    ? "text-[#1a3a2a] hover:bg-[#f8f9f8]"
                    : "text-white hover:bg-white/10"
              }`}
            >
              EN
            </button>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 ${scrolled ? "text-[#1a3a2a]" : "text-white"}`}
            aria-label="menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="nav-mobile-drawer"
          className="lg:hidden bg-white border-t border-[#1a3a2a]/10"
        >
          <div className="pb-container py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="text-left font-heading text-2xl font-bold text-[#1a3a2a] uppercase tracking-tight"
              >
                {t.nav[l.key]}
              </button>
            ))}
            <div className="flex items-center gap-2 pt-4 border-t border-[#1a3a2a]/10">
              <Globe className="w-4 h-4 text-[#1a3a2a]" strokeWidth={1.5} />
              <button
                onClick={() => setLang("it")}
                data-testid="mobile-lang-it"
                className={`px-3 py-1.5 text-xs font-semibold ${
                  lang === "it" ? "bg-[#1a3a2a] text-white" : "text-[#1a3a2a]"
                }`}
              >
                IT
              </button>
              <button
                onClick={() => setLang("en")}
                data-testid="mobile-lang-en"
                className={`px-3 py-1.5 text-xs font-semibold ${
                  lang === "en" ? "bg-[#1a3a2a] text-white" : "text-[#1a3a2a]"
                }`}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
